import ArticleLayout from '../../components/ArticleLayout';

export default function RadiatorOgVarmepumpe() {
  return (
    <ArticleLayout
      title="Radiatorer med varmepumpe: Hvorfor de føles lunkne, og hvordan de indstilles"
      metaTitle="Radiator og varmepumpe: Hvorfor de føles lunkne"
      metaDescription="Har du fået varmepumpe, og føles radiatorerne lunkne? Lær hvorfor lav fremløbstemperatur er optimalt, og hvordan du indstiller termostaterne."
      canonicalPath="/radiator-og-varmepumpe"
      introLead="Hvis du for nylig har skiftet fra olie- eller gasfyr til en varmepumpe, vil dine radiatorer sjældent føles rygende varme. De føles oftest blot let lunkne til håndvarme."
      introAnswer="Dette er helt bevidst og den mest energieffektive måde at opvarme på. En varmepumpe arbejder med lav fremløbstemperatur på 35–48 °C, hvor et ældre fyr sendte 65–75 °C hedt vand ud i rørene. Så længe stuetemperaturen holder 20–21 °C, fungerer anlægget perfekt."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: Korrekt indstilling med varmepumpe' },
        { id: 'lavtemperatur', title: '2. Lav fremløbstemperatur og COP-værdi' },
        { id: 'termostat-fejl', title: '3. Den klassiske termostatfejl ved varmepumpe' },
        { id: 'stoerrelse', title: '4. Er dine eksisterende radiatorer store nok?' },
        { id: 'faq', title: '5. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: 5 gyldne regler for radiatorer med varmepumpe"
      checklist={[
        {
          title: 'Hold termostaterne konstant åbne i opholdsrum',
          description: 'Sæt termostaterne i stue og køkken på trin 3–4 og lad være med at skrue op og ned hele tiden.',
        },
        {
          title: 'Undgå store natsænkninger på mere end 1–2 °C',
          description: 'Varmepumper opvarmer langsomt og taber effektivitet, hvis de pludselig skal indhente 4 graders temperaturfald om morgenen.',
        },
        {
          title: 'Styr rumtemperaturen via varmepumpens varmekurve',
          description: 'Er huset for koldt eller for varmt, justeres fremløbskurven på varmepumpens display – ikke på de enkelte radiatorer.',
        },
        {
          title: 'Sørg for uhindret luftstrøm omkring radiatorerne',
          description: 'Ved lav fremløbstemperatur er fri konvektion afgørende; fjern afskærmninger og hold mindst 20 cm frirum.',
        },
        {
          title: 'Sørg for korrekt hydraulisk indregulering',
          description: 'Hver radiator skal have indstillet sin forindstilling, så flowet fordeles jævnt i hele boligen.',
        },
      ]}
      sections={[
        {
          id: 'lavtemperatur',
          title: 'Lav fremløbstemperatur og varmepumpens effektivitet',
          content: (
            <>
              <p>
                En varmepumpes virkningsgrad måles i SCOP/COP (hvor mange kilowatt-timer varme den leverer pr. brugt kilowatt-time strøm). Hver gang du sænker fremløbstemperaturen med <span className="font-mono">1 °C</span>, forbedres varmepumpens effektivitet med ca. 2–3 procent.
              </p>
              <p>
                Derfor indstilles en luft-til-vand eller jordvarmepumpe til altid at levere den lavest mulige vandtemperatur, der lige akkurat kan holde boligen varm ved den aktuelle udetemperatur. En radiator ved <span className="font-mono">40 °C</span> afgiver rigelig varme, hvis den har en passende overflade, men den vil føles lunken ved berøring med hånden (kroppen er ca. <span className="font-mono">37 °C</span>).
              </p>
            </>
          ),
        },
        {
          id: 'termostat-fejl',
          title: 'Den klassiske termostatfejl',
          content: (
            <>
              <p>
                I et traditionelt fyr-system skruede man op og ned for termostaterne efter behov. Med en varmepumpe er denne metode uhensigtsmæssig.
              </p>
              <p>
                Hvis du lukker termostaterne, falder vandgennemstrømningen over varmepumpen. Varmepumpen begynder at starte og stoppe hyppigt (såkaldt „taktning“), hvilket slider på kompressoren og forhøjer elforbruget. Lad radiatorerne stå åbne på ønsket temperatur, så varmepumpen har en jævn og stabil vandvolumen at afsætte varmen i.
              </p>
            </>
          ),
        },
        {
          id: 'stoerrelse',
          title: 'Er dine eksisterende radiatorer store nok?',
          content: (
            <>
              <p>
                I mange ældre huse med olie- eller gasfyr blev radiatorerne oprindeligt dimensioneret til <span className="font-mono">70/40 °C</span> drift. Når fremløbstemperaturen sænkes til <span className="font-mono">45–50 °C</span>, falder radiatorens varmeafgivelse til under det halve pr. kvadratmeter overflade.
              </p>
              <p>
                Hvis et rum – typisk et hjørnerum med store vinduer – ikke kan nå 20 °C på kolde vinterdage, er radiatoren for lille. Løsningen er at udskifte den enkeltstående radiator til en moderne type 22 eller type 33 med flere konvektionslameller, frem for at hæve varmepumpens temperatur for hele huset.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Hvornår skal varmepumpe-installatøren tilkaldes?',
        content: (
          <p>
            Justering af varmepumpens varmekurve, indstilling af differenstryk og dimensionering af nye lavtemperaturradiatorer bør altid foretages i samråd med en autoriseret varmepumpeinstallatør. Ændr aldrig interne fabriksindstillinger eller kølemiddelkredse på egen hånd.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Kan man bruge eksisterende radiatorer til en varmepumpe?',
          a: 'Ja, i mange velisolerede huse er de eksisterende radiatorer tilstrækkeligt store. I dårligt isolerede rum kan det dog være nødvendigt at udskifte enkelte radiatorer til kraftigere modeller med flere lameller.',
        },
        {
          q: 'Hvorfor må jeg ikke lave stor natsænkning med varmepumpe?',
          a: 'Varmepumpen arbejder mest økonomisk med et stabilt, lavt flow døgnet rundt. En stor natsænkning tvinger varmepumpen til at køre på fuld kraft med elpatron-tilskud om morgenen, hvilket ødelægger strømbesparelsen.',
        },
        {
          q: 'Hvad er en lavtemperaturradiator?',
          a: 'En lavtemperaturradiator har større overfladeareal og tætte konvektionslameller, så den kan afgive tilstrækkelig varme til rummet ved fremløbstemperaturer på 35–45 °C.',
        },
      ]}
      internalLinks={[
        { to: '/radiator-varmer-ikke', text: 'Radiatoren varmer ikke', desc: 'grundlæggende fejlsøgning på radiatorer' },
        { to: '/radiator-kold-i-bunden', text: 'Radiator kold i bunden', desc: 'forstå temperaturfordeling og afkøling' },
        { to: '/spar-paa-varmen', text: 'Spar på varmen', desc: 'gode råd til energieffektiv drift' },
        { to: '/radiator-udskiftning-pris', text: 'Pris på udskiftning af radiator', desc: 'omkostninger ved nye lavtemperaturradiatorer' },
      ]}
      sources={[
        { url: 'https://sparenergi.dk/privat/spar-energi-i-hverdagen/spar-paa-varme-og-vand-i-hjemmet', label: 'Normalt varmeforbrug i kWh (+ fjernvarme i MWh) | SparEnergi' },
        { url: 'https://www.bolius.dk/valg-af-radiator-19095', label: 'Valg af radiator - sådan sikrer du, at du vælger rigtigt' },
      ]}
    />
  );
}
